import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Logo } from '@/components/ui/Logo'
import LanguageSwitcher from '@/components/ui/LanguageSwitcher'
import { useTheme } from '@/hooks/useTheme'
import { useAuth } from '@/contexts/AuthContext'
import { ProductHuntBanner } from '@/components/marketing/ProductHuntBanner'
import { FREE_TOOLS } from '@/data/tools'
import {
    SunIcon,
    MoonIcon,
    Bars3Icon,
    XMarkIcon,
    ChevronDownIcon,
    ArrowRightIcon,
} from '@heroicons/react/24/outline'

/**
 * Total vertical space the fixed header occupies: the 15px top gap plus the
 * 86px bar. Pages offset their first section by this, and the occlusion test
 * asserts against it, so the number lives in exactly one place.
 */
export const PUBLIC_HEADER_HEIGHT_PX = 101

const LINK =
    'rounded-md text-[15px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900'
const LINK_IDLE = 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
const LINK_ACTIVE = 'text-primary-600 dark:text-primary-400'
const ICON_BUTTON =
    'p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
const PRIMARY_CTA =
    'inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 text-[15px] font-medium text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 group'

/**
 * The single top navigation for every public page.
 *
 * Replaces LandingHeader, ToolsNav, and four hand-copied inline navs. Three of
 * those inline copies shipped without a language switcher, and the landing
 * header hardcoded three of the five tools — both classes of bug are only
 * possible when the nav exists in more than one place, so it now exists once.
 *
 * The Free Tools menu is generated from FREE_TOOLS: adding a tool to the
 * registry publishes it to every public page at once.
 */
export default function PublicHeader() {
    const { t } = useTranslation()
    const { pathname } = useLocation()
    const { isAuthenticated } = useAuth()
    const toolsButtonRef = useRef<HTMLButtonElement>(null)
    const { theme, toggleTheme } = useTheme()
    const [isScrolled, setIsScrolled] = useState(false)
    const [isVisible, setIsVisible] = useState(true)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [isToolsOpen, setIsToolsOpen] = useState(false)
    const [mobileToolsOpen, setMobileToolsOpen] = useState(false)

    useEffect(() => {
        let lastScrollY = window.scrollY

        const handleScroll = () => {
            const currentScrollY = window.scrollY
            setIsScrolled(currentScrollY > 20)

            // Hide when scrolling down past 100px, show again on any scroll up.
            if (currentScrollY > lastScrollY && currentScrollY > 100) {
                setIsVisible(false)
            } else if (currentScrollY < lastScrollY) {
                setIsVisible(true)
            }

            lastScrollY = currentScrollY
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (!isToolsOpen) return

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsToolsOpen(false)
                toolsButtonRef.current?.focus()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isToolsOpen])

    useEffect(() => {
        if (!mobileMenuOpen) return

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMobileMenuOpen(false)
        }
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [mobileMenuOpen])

    const isToolsRoute = pathname === '/tools' || pathname.startsWith('/tools/')
    const navLink = (active: boolean) => `${LINK} ${active ? LINK_ACTIVE : LINK_IDLE}`
    const mobileLink = (active: boolean) =>
        `px-3 py-3 rounded-xl text-[15px] font-semibold transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            active ? LINK_ACTIVE : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
        }`

    const themeIcon =
        theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />

    return (
        <>
            <header
                className={`fixed top-0 w-full z-50 pointer-events-none flex flex-col transition-transform duration-300 ease-in-out ${
                    isVisible || mobileMenuOpen || isToolsOpen ? 'translate-y-0' : '-translate-y-[150%]'
                }`}
            >
                <ProductHuntBanner />
                <div className="w-full pt-[15px] px-4 sm:px-8 flex justify-center">
                <nav
                    aria-label={t('nav.public.mainNavigation')}
                    className={`w-full max-w-[1626px] mx-auto rounded-[20px] transition-all duration-300 pointer-events-auto ${
                        isScrolled
                            ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg border border-gray-200 dark:border-gray-800'
                            : 'bg-white dark:bg-gray-900 shadow-md border border-gray-100 dark:border-gray-800'
                    }`}
                >
                    <div className="flex justify-between items-center h-[86px] px-6 lg:px-8">
                        {/* Left: logo + desktop links */}
                        <div className="flex items-center space-x-12">
                            <Link
                                to="/"
                                aria-label={t('nav.public.home')}
                                className="flex items-center space-x-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
                            >
                                <Logo className="h-[38px] md:h-[47px] transition-transform hover:scale-105" />
                            </Link>

                            <div className="hidden md:flex items-center space-x-8">
                                <a href="/#features" className={navLink(false)}>
                                    {t('nav.public.features')}
                                </a>
                                <Link
                                    to="/pricing"
                                    aria-current={pathname === '/pricing' ? 'page' : undefined}
                                    className={navLink(pathname === '/pricing')}
                                >
                                    {t('nav.public.pricing')}
                                </Link>

                                {/* Free Tools dropdown, generated from the registry */}
                                <div
                                    className="relative"
                                    onMouseEnter={() => setIsToolsOpen(true)}
                                    onMouseLeave={() => setIsToolsOpen(false)}
                                    onBlur={(e) => {
                                        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsToolsOpen(false)
                                    }}
                                >
                                    <button
                                        ref={toolsButtonRef}
                                        type="button"
                                        className={`flex items-center space-x-1 ${navLink(isToolsRoute)}`}
                                        onClick={() => setIsToolsOpen((open) => !open)}
                                        aria-expanded={isToolsOpen}
                                        aria-controls="public-tools-menu"
                                    >
                                        <span>{t('nav.public.freeTools')}</span>
                                        <ChevronDownIcon
                                            className={`h-4 w-4 transition-transform duration-200 ${isToolsOpen ? 'rotate-180' : ''}`}
                                        />
                                    </button>

                                    {/* pt-2 instead of a margin: the padding is part of the
                                        hover target, so moving the pointer from the button
                                        down to the panel no longer crosses a dead gap that
                                        fired mouseleave and closed the menu. */}
                                    <div
                                        id="public-tools-menu"
                                        className={`absolute left-0 top-full pt-2 w-72 transform transition-all duration-200 origin-top-left z-50 ${
                                            isToolsOpen
                                                ? 'opacity-100 scale-100 visible'
                                                : 'opacity-0 scale-95 invisible'
                                        }`}
                                    >
                                        <div className="py-2 rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-100 dark:border-gray-700">
                                            {FREE_TOOLS.map((tool) => {
                                                const isActive = tool.path === pathname
                                                return (
                                                    <Link
                                                        key={tool.path}
                                                        to={tool.path}
                                                        onClick={() => setIsToolsOpen(false)}
                                                        aria-current={isActive ? 'page' : undefined}
                                                        className={`block px-4 py-3 text-sm focus-visible:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-700/50 ${
                                                            isActive
                                                                ? 'bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400'
                                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-primary-600 dark:hover:text-primary-400'
                                                        }`}
                                                    >
                                                        <div className="font-medium">{t(tool.nameKey)}</div>
                                                        <div
                                                            className={`text-xs mt-0.5 ${isActive ? 'text-primary-400' : 'text-gray-500 dark:text-gray-500'}`}
                                                        >
                                                            {t(tool.outcomeKey)}
                                                        </div>
                                                    </Link>
                                                )
                                            })}
                                            <div className="mt-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                                                {pathname === '/tools' ? (
                                                    <span className="block px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400">
                                                        {t('nav.public.allTools')}
                                                    </span>
                                                ) : (
                                                    <Link
                                                        to="/tools"
                                                        onClick={() => setIsToolsOpen(false)}
                                                        className="block px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus-visible:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-700/50"
                                                    >
                                                        {t('nav.public.viewAllTools')}
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <Link
                                    to="/blogs"
                                    aria-current={pathname === '/blogs' ? 'page' : undefined}
                                    className={navLink(pathname.startsWith('/blog'))}
                                >
                                    {t('nav.public.blog')}
                                </Link>
                            </div>
                        </div>

                        {/* Right: desktop actions */}
                        <div className="hidden md:flex items-center space-x-4">
                            <button
                                type="button"
                                onClick={toggleTheme}
                                className={ICON_BUTTON}
                                aria-label={t('common.toggleTheme')}
                            >
                                {themeIcon}
                            </button>
                            <LanguageSwitcher />

                            <div className="h-6 w-px bg-gray-300 dark:bg-gray-700 mx-2" />

                            {isAuthenticated ? (
                                <Link to="/boards" className={`${PRIMARY_CTA} px-6 py-2.5`}>
                                    {t('nav.dashboard')}
                                    <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        className={`${LINK} px-2 text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300`}
                                    >
                                        {t('auth.login')}
                                    </Link>
                                    <Link to="/register" className={`${PRIMARY_CTA} px-6 py-2.5`}>
                                        {t('landing.hero.cta')}
                                        <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                    </Link>
                                </>
                            )}
                        </div>

                        {/* Right: mobile actions. The switcher and toggle stay here
                            too — ToolsNav kept them desktop-only, which left mobile
                            visitors on tool pages with no way to change language. */}
                        <div className="md:hidden flex items-center space-x-2">
                            <LanguageSwitcher />
                            <button
                                type="button"
                                onClick={toggleTheme}
                                className={ICON_BUTTON}
                                aria-label={t('common.toggleTheme')}
                            >
                                {themeIcon}
                            </button>
                            <button
                                type="button"
                                onClick={() => setMobileMenuOpen((open) => !open)}
                                className={ICON_BUTTON}
                                aria-label={
                                    mobileMenuOpen ? t('nav.public.closeMenu') : t('nav.public.openMenu')
                                }
                                aria-expanded={mobileMenuOpen}
                            >
                                {mobileMenuOpen ? (
                                    <XMarkIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                                ) : (
                                    <Bars3Icon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                                )}
                            </button>
                        </div>
                    </div>
                </nav>
                </div>
            </header>

            {/* Mobile menu, rendered outside the fixed pill so it can overlay it */}
            {mobileMenuOpen && (
                <>
                    <div
                        aria-hidden="true"
                        className="fixed inset-0 z-40 bg-gray-900/20 backdrop-blur-sm"
                        onClick={() => setMobileMenuOpen(false)}
                    />

                    <div className="md:hidden fixed left-4 right-4 z-50" style={{ top: 'calc(100px + var(--promo-banner-h, 0px))' }}>
                        <div className="rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-100 dark:border-gray-800 max-h-[calc(100vh-120px)] overflow-y-auto">
                            <div className="flex flex-col px-4 py-4 space-y-1">
                                <a
                                    href="/#features"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={mobileLink(false)}
                                >
                                    {t('nav.public.features')}
                                </a>
                                <Link
                                    to="/pricing"
                                    onClick={() => setMobileMenuOpen(false)}
                                    aria-current={pathname === '/pricing' ? 'page' : undefined}
                                    className={mobileLink(pathname === '/pricing')}
                                >
                                    {t('nav.public.pricing')}
                                </Link>

                                <div>
                                    <button
                                        type="button"
                                        onClick={() => setMobileToolsOpen((open) => !open)}
                                        className={`flex items-center justify-between w-full ${mobileLink(isToolsRoute)}`}
                                        aria-expanded={mobileToolsOpen}
                                    >
                                        <span>{t('nav.public.freeTools')}</span>
                                        <ChevronDownIcon
                                            className={`h-4 w-4 transition-transform duration-200 ${mobileToolsOpen ? 'rotate-180' : ''}`}
                                        />
                                    </button>
                                    {mobileToolsOpen && (
                                        <div className="pl-4 mt-1 space-y-1 border-l-2 border-gray-100 dark:border-gray-800 ml-3">
                                            {FREE_TOOLS.map((tool) => (
                                                <Link
                                                    key={tool.path}
                                                    to={tool.path}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className={`block px-3 py-2.5 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 ${
                                                        tool.path === pathname
                                                            ? 'text-primary-600 dark:text-primary-400 font-medium'
                                                            : 'text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400'
                                                    }`}
                                                >
                                                    {t(tool.nameKey)}
                                                </Link>
                                            ))}
                                            {pathname !== '/tools' && (
                                                <Link
                                                    to="/tools"
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className="block px-3 py-2.5 text-sm font-medium text-primary-600 dark:text-primary-400"
                                                >
                                                    {t('nav.public.viewAllTools')}
                                                </Link>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <Link
                                    to="/blogs"
                                    onClick={() => setMobileMenuOpen(false)}
                                    aria-current={pathname === '/blogs' ? 'page' : undefined}
                                    className={mobileLink(pathname.startsWith('/blog'))}
                                >
                                    {t('nav.public.blog')}
                                </Link>

                                <div className="border-t border-gray-100 dark:border-gray-800 pt-3 mt-2 flex flex-col gap-2">
                                    {isAuthenticated ? (
                                        <Link
                                            to="/boards"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={`${PRIMARY_CTA} w-full rounded-xl py-3`}
                                        >
                                            {t('nav.dashboard')}
                                        </Link>
                                    ) : (
                                        <>
                                            <Link
                                                to="/login"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="block text-center px-4 py-3 rounded-xl text-[15px] font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                                            >
                                                {t('auth.login')}
                                            </Link>
                                            <Link
                                                to="/register"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className={`${PRIMARY_CTA} w-full rounded-xl py-3`}
                                            >
                                                {t('landing.hero.cta')}
                                            </Link>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    )
}
